import { z } from "zod";

import { ChatStreamEventSchema } from "./chat-stream-events.types";

/**
 * Wire shape for the conversation-scoped subscription.
 * Unlike the per-turn SSE (`ChatStreamEvent` only, closes on `message-end`),
 * this stream never stops at a turn boundary — it forwards the durable log's
 * segment markers as their own frames so the client can drive a turn-state
 * machine across turns, holds, and idle gaps on ONE cursor.
 *
 * - `turn-start` / `turn-end` mirror the host-only markers
 *   `chatStreamEventCodec` decodes from the log (`DecodedStreamLogEntry`) —
 *   never a `ChatStreamEvent` themselves, so they need their own frame kind
 *   to cross the wire instead of being folded into `type`.
 * - `resync` mirrors `ConsumeDurableStreamService`'s `onTrimGap` signal: the
 *   requested cursor was older than the log's trimmed floor, so entries were
 *   evicted before the client saw them. The client must resync out-of-band
 *   (e.g. refetch the message page) rather than render a hole.
 * - `event` carries every ordinary `ChatStreamEvent` unchanged.
 */
export const ConversationStreamFrameSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("turn-start"), generationId: z.string() }),
  z.object({ kind: z.literal("turn-end"), generationId: z.string().optional() }),
  z.object({ kind: z.literal("resync") }),
  z.object({ kind: z.literal("event"), event: ChatStreamEventSchema })
]);

export type ConversationStreamFrame = z.infer<typeof ConversationStreamFrameSchema>;
